export type BriefingFilter = 'all' | 'first-time' | 'returning' | 'attention'

type BriefingGreetingProps = {
  // Family name for "Dr. <name>", else the full name.
  doctorName: string | null
  // Epoch ms, ticking once a minute.
  now: number
  visits: number
  firstTime: number
  returning: number
  attention: number
  // A source failed to load, so an empty brief is not proof that nothing needs the provider.
  someUnavailable: boolean
  filter: BriefingFilter
  onFilterChange: (filter: BriefingFilter) => void
}

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

type Chip = { id: BriefingFilter; count: number; label: string; countClassName: string }

/** The greeting and one line of counts; each count filters both columns. Zero counts hide. */
export function BriefingGreeting({
  doctorName,
  now,
  visits,
  firstTime,
  returning,
  attention,
  someUnavailable,
  filter,
  onFilterChange,
}: BriefingGreetingProps) {
  // Zero chips are hidden, except the active one: a filter never hides visits unseen.
  const chips: Chip[] = [
    {
      id: 'all' as const,
      count: visits,
      label: visits === 1 ? 'visit today' : 'visits today',
      countClassName: 'text-gray-900',
    },
    {
      id: 'first-time' as const,
      count: firstTime,
      label: firstTime === 1 ? 'first-time patient' : 'first-time patients',
      countClassName: 'text-gray-900',
    },
    {
      id: 'returning' as const,
      count: returning,
      label: returning === 1 ? 'returning patient' : 'returning patients',
      countClassName: 'text-gray-900',
    },
    {
      id: 'attention' as const,
      count: attention,
      label: attention === 1 ? 'item needs review' : 'items need review',
      countClassName: 'text-[#e55535]',
    },
  ].filter((chip) => chip.id === 'all' || chip.id === filter || chip.count > 0)

  return (
    <div className="border-b border-gray-100 bg-gradient-to-b from-[#fffaf8] to-white px-4 pt-4 pb-3.5">
      <h3 className="mb-2.5 text-[22px] font-bold tracking-[-0.2px] text-gray-900">
        {greeting(new Date(now).getHours())}
        {doctorName ? `, Dr. ${doctorName}.` : '.'}
      </h3>
      {/* The size sits on the wrapper: the global `button { font: inherit }` reset is unlayered,
          so it beats a Tailwind size class on the button itself. */}
      <div className="flex flex-wrap items-center gap-2 text-sm leading-normal">
        {chips.map((chip) => {
          const on = filter === chip.id
          return (
            // A plain button: Mantine's UnstyledButton resets the border these chips draw.
            <button
              type="button"
              key={chip.id}
              aria-pressed={on}
              title={chip.id === 'all' ? 'Show everyone' : 'Show only these'}
              onClick={() => onFilterChange(chip.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-gray-800 ${
                on
                  ? 'border-gray-700 bg-gray-50 shadow-[inset_0_0_0_1px_#374151]'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <b className={`text-[15px] tabular-nums ${chip.countClassName}`}>{chip.count}</b>
              {chip.label}
            </button>
          )
        })}
        {attention === 0 && (
          <span className="text-sm text-gray-600">
            {someUnavailable
              ? 'Nothing found in the sources that loaded.'
              : 'Everything that needed you is done.'}
          </span>
        )}
      </div>
    </div>
  )
}
